<!--
 * @Description: 开票-未开票列表
 * @Date: 2025-06-11 16:21:13
-->

<template>
  <!-- v-show防止重复渲染与丢失ref已选项 -->
  <XrkTable
    v-show="!props.disabled"
    ref="xrkTableRef"
    :requestNow="false"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  >
    <template #tableBefore>
      <div>
        <span style="margin-right: 15px">
          价税合计：{{ formatter.fen2yuan(selectionUserDefinedPrice) }}元
        </span>
        <span>最大开票金额：{{ formatter.fen2yuan(totalPrice) }}元</span>
      </div>
    </template>
  </XrkTable>
  <div v-show="props.disabled">
    <div>
      <span style="margin-right: 15px">
        价税合计：{{ formatter.fen2yuan(selectionUserDefinedPrice) }}元
      </span>
      <span>最大开票金额：{{ formatter.fen2yuan(totalPrice) }}元</span>
    </div>
    <BaseTable :data="selections" :columns="tableConfig.tableColumns" />
  </div>
</template>
<script lang="tsx" setup>
import { ref, computed, watch } from 'vue';
import {
  BaseTable,
  XrkTable,
  XrkTableProps,
  useTableColumn,
  BaseButton
} from 'xrk-components';
import { formatter } from '@/utils';
import { PAY_PASSAGEWAY_MAPPING } from '@/common/constants/bank';
import { notInvoicedList } from '@V/invoice/src/data-source/apis';
import { ORDER_TYPE, PAY_TYPE } from '@constants/order';
import { taxPatternMapping } from '@constants/project';
import {
  MakeOutAnInvoiceParams,
  NotInvoicedListParams
} from '@V/invoice/src/data-source/types';
import { useJumpToFinishedOrderList, useJumpToOrderDetail } from '@hooks/order';
import { IS_PLATFORM_TYPE_1, IS_ASSISTANT } from '@/config';

type FormData = MakeOutAnInvoiceParams & NotInvoicedListParams;

const props = withDefaults(
  defineProps<{
    disabled: boolean;
    contentRef: any;
  }>(),
  {}
);

const xrkTableRef = ref();
const formData = defineModel<FormData>();
const totalPrice = ref(0);

const selections = computed(() => {
  return xrkTableRef.value?.getSelectionRows?.() || [];
});
const selectionUserDefinedPrice = computed(() => {
  return selections.value.reduce((pre, cur) => {
    return pre + cur.userDefinedPrice;
  }, 0);
});

watch(
  () => selections.value,
  async v => {
    if (v.length) {
      const ids = v.map(item => item.openOrderId);
      formData.value.openOrderIds = ids;
    } else {
      formData.value.openOrderIds = [];
    }
  }
);
const getListAjax = async params => {
  const resp = await notInvoicedList({ ...params, ...formData.value });
  totalPrice.value = resp.data.totalPrice;
  return resp;
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [],
  searchBtn: {
    show: true,
    text: '搜索',
    async beforeSearch() {
      await props.contentRef.validate();
    }
  },
  resetBtn: {
    show: false,
    text: '重置'
  }
});

const tableConfig = computed(() => {
  return {
    tableColumns: [
      !props.disabled &&
        useTableColumn({
          type: '_selection',
          prop: 'openOrderId',
          align: 'center',
          fixed: 'left',
          width: 110
        }),
      // useTableColumn({
      //   type: 'index',
      //   label: '序号',
      //   width: '80px'
      // }),
      useTableColumn({
        label: '购买方',
        prop: 'purchaserName',
        width: '180px'
      }),
      useTableColumn({
        label: '项目名称',
        prop: 'projectName',
        width: '180px'
      }),
      IS_PLATFORM_TYPE_1 &&
        useTableColumn({
          label: '交易结构',
          prop: 'taxPattern',
          formatter: ({ cellValue }) =>
            formatter.defaultFormat(
              cellValue,
              () => taxPatternMapping[cellValue].label
            )
        }),
      useTableColumn({
        label: '订单编号',
        prop: 'userDefinedOrderNo',
        width: '260px',
        render({ row }) {
          return IS_ASSISTANT ? (
            <span>{row.userDefinedOrderNo}</span>
          ) : (
            <BaseButton
              link
              onClick={() => {
                useJumpToFinishedOrderList(row.userDefinedOrderNo, {
                  pageName: 'finished',
                  method: 'open'
                });
              }}
            >
              {row.userDefinedOrderNo}
            </BaseButton>
          );
        }
      }),
      useTableColumn({
        label: '订单类型',
        prop: 'userDefinedOrderType',
        formatter: ({ cellValue }) =>
          formatter.defaultFormat(cellValue, () => ORDER_TYPE[cellValue].label)
      }),
      useTableColumn({
        label: '销售方',
        prop: 'theSellerName',
        width: '180px'
      }),
      useTableColumn({
        label: '收款时间',
        prop: 'receiveAt',
        width: '180px',
        formatter: ({ cellValue }) => formatter.date(cellValue)
      }),
      useTableColumn({
        label: '支付通道',
        prop: 'payPassagewayCode',
        formatter: ({ cellValue }) =>
          formatter.defaultFormat(
            cellValue,
            () => PAY_PASSAGEWAY_MAPPING[cellValue].label
          )
      }),
      useTableColumn({
        label: '付款方式',
        prop: 'payType',
        formatter: ({ cellValue }) =>
          formatter.defaultFormat(cellValue, () => PAY_TYPE[cellValue].label)
      }),
      useTableColumn({
        label: '金额',
        prop: 'userDefinedPrice',
        width: '180px',
        render({ row }) {
          const _userDefinedPrice = formatter.fen2yuan(row.userDefinedPrice);
          return IS_ASSISTANT ? (
            <span>{_userDefinedPrice}</span>
          ) : (
            <BaseButton
              link
              onClick={() => {
                useJumpToOrderDetail(row.userDefinedOrderNo, {
                  method: 'open'
                });
              }}
            >
              {_userDefinedPrice}
            </BaseButton>
          );
        }
      })
    ].filter(Boolean)
  };
});
</script>
